import { CSSProperties, useState } from 'react'
import { SmartForm } from '../../components/formFields/Form'
import { TurnoStatus } from '../../data/types/turnos.type'
import { useTurnoMedico } from '../../data/hooks/useTurnos.hook'
import { useParams } from 'react-router-dom'
import { Spinner } from '../../components/spinner/Spinner.component'
import { formatDateToString, formatHourWithoutSeconds, formatStringToUserFullDate } from '../../helpers/dates.helper'
import { formatCurrency } from '../../helpers/general.helper'
import { ButtonVariants, SmartButton } from '../../components/buttons/GralButton'
import { Historia } from '../../data/types/historia.types'
import { api } from '../../data/api'
import toast from 'react-hot-toast'
import { HistoriaCard } from './HistoriaCard'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../auth/AuthContext'
import { Role } from '../../data/types/medico.types'
import CustomModal from '../../components/modal/CustomModal'
import { LinkInApp } from '../../pagesApp/LinkInApp.component'

const RowsContainer: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: '10px',
}

const Row1Box: CSSProperties = {
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	alignItems: 'baseline',
	gap: '10px',
}

const badgeStyle = (color: string): React.CSSProperties => ({
	backgroundColor: color,
	borderRadius: '8px',
	padding: '4px 8px',
	fontSize: '12px',
	fontWeight: 'bold',
	color: '#e2e8f0',
})

const Badge = ({ status }: { status: TurnoStatus }) => {

	switch (status) {
		case TurnoStatus.PENDIENTE:
			return <span style={badgeStyle('#0a7415')}>{'Confirmado'}</span>
		case TurnoStatus.IMPAGO:
			return <span style={badgeStyle('#94a11f')}>{'Solicitado'}</span>
		case TurnoStatus.CANCELADO:
			return <span style={badgeStyle('#921919')}>{'Cancelado'}</span>
		case TurnoStatus.COMPLETADO:
			return <span style={badgeStyle('#10217c')}>{'Completado'}</span>
		default:
			return null
	}
}

const getFechaTurno = (fecha: string, hora: string) => {
	const today = formatDateToString(new Date())
	if (fecha === today) {
		return `Hoy (${formatStringToUserFullDate(fecha)} a las ${formatHourWithoutSeconds(hora)})`
	}
	return `${formatStringToUserFullDate(fecha)} a las ${formatHourWithoutSeconds(hora)}`
}

export const TurnoPage = () => {
	const today = formatDateToString(new Date())

	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const [showCancelModal, setShowCancelModal] = useState(false)
	const [historiaClinica, setHistoriaClinica] = useState('')
	const { id } = useParams()
	const { role } = useAuth()
	const { data: turno, isPending } = useTurnoMedico(Number(id))
	const queryClient = useQueryClient()
	const [showJitsiModal, setShowJitsiModal] = useState(false)

	const handleSaveHistoria = async () => {
		if (!turno) return

		const todayString = formatDateToString(new Date())

		if (role === Role.ADMIN) {
			toast.error('Solo un médico puede agregar historia clínica')
			return
		}

		if (turno.statusTurno !== TurnoStatus.PENDIENTE || todayString !== turno.fecha) {
			toast.error('Solo se puede agregar historia el día del turno')
			return
		}

		if (!historiaClinica.length) {
			toast.error('La historia clínica no puede estar vacía')
			return
		}

		try {
			
			const newHistoria: Historia = {
				pacienteDni: turno.pacienteDni,
				medicoDni: turno.medicoDni,
				fecha: turno.fecha,
				notas: historiaClinica,
			}
			
			await api.post(`/api/historia/new`, newHistoria)
			toast.success('Historia clínica guardada correctamente')
			setHistoriaClinica('')
			await queryClient.invalidateQueries({ queryKey: ['turno',Number(id)] })
			await queryClient.refetchQueries({ queryKey: ['turno', Number(id)] })
		} catch (error) {
			console.error("### error", error)
			toast.error('Error al guardar la historia clínica')
		}
	}

	const handleCancelarTurno = async () => {
		if (!turno) return
		setShowCancelModal(true)
	}

	const handleCancelarTurnoConfirmed = async () => {
		if (!turno) return

		try {
			await api.post(`/api/turnos/cancelarTurno/${turno.id}/${role === Role.ADMIN ? 'A' : 'M'}`)
			setShowCancelModal(false)
			toast.success('Turno cancelado correctamente')
			await queryClient.invalidateQueries({ queryKey: ['turno',Number(id)] })
			await queryClient.refetchQueries({ queryKey: ['turno', Number(id)] })
		} catch (error) {
			setShowCancelModal(false)
			console.error("### error", error)
			toast.error('Error al cancelar el turno')
		}
	}

	const handleFinalizarTurno = async () => {
		setShowCancelModal(false)
		if (!turno) return
		if (turno.statusTurno !== TurnoStatus.PENDIENTE) {
			toast.error('Solo se puede finalizar un turno pendiente')
			return
		}
		
		try {
			await api.put(`/api/turnos/status/${turno.id}/${TurnoStatus.COMPLETADO}`)
			toast.success('Turno cancelado correctamente')
			await queryClient.invalidateQueries({ queryKey: ['turno',Number(id)] })
			await queryClient.refetchQueries({ queryKey: ['turno', Number(id)] })
		} catch (error) {
			console.error("### error", error)
			toast.error('Error al cancelar el turno')
		}
	}

	const handleGenerarLinkLlamada = async () => {
		if (!turno) return
		await api.get(`/api/google/createLink/${turno?.id}`)
		await queryClient.invalidateQueries({ queryKey: ['turno',Number(id)] })
		await queryClient.refetchQueries({ queryKey: ['turno', Number(id)] })
	}

	if (isPending) return <Spinner />

	if (!turno) return <div>Turno no encontrado</div>

	return (
		<>
		<SmartForm
			onSubmit={() => {}}
			width='90%'
			adaptToViewport={true}
		>
			<div style={RowsContainer}>
				<div style={Row1Box}>
					<div style={{width: '25%'}}>
						<h2>{`Turno `}
							<Badge status={turno.statusTurno} />
						</h2> 
						<p>{getFechaTurno(turno.fecha, turno.hora)}</p>
						{turno.statusTurno === TurnoStatus.CANCELADO && <p>{`Turno cancelado por el ${turno.canceladoPor === 'P' ? 'Paciente' : 'Médico'}`}</p>}
						{(turno.statusTurno === TurnoStatus.PENDIENTE) && today !== turno.fecha && 
							<SmartButton
								onClick={handleCancelarTurno}
							>
								Cancelar Turno
							</SmartButton>
						}
						{(today === turno.fecha && turno.statusTurno === TurnoStatus.PENDIENTE) &&
							<>
								<p style={{marginBottom: '15px'}}>
									{turno.linkLlamada &&
										<a href={turno.linkLlamada} target='_blank'>
											Abrir Videollamada en Google Meet
										</a>
									}
									{!turno.linkLlamada && 
										<LinkInApp 
											onClick={handleGenerarLinkLlamada} 
											label='Generar Link de Videollamada' 
										/>
									}
								</p>
								<div style={{display: 'flex', flexDirection: 'row', gap: '10px'}}>
									<SmartButton 
										width='45%'
										onClick={() => setShowConfirmModal(true)}
									>
										Turno Finalizado Exitosamente
									</SmartButton>
								</div>
							</>
						}
					</div>
					<div style={{width: '40%'}}>
						<h2>Paciente</h2>
						<p>
							{turno.pacienteNombre} (DNI: {turno.pacienteDni})<br />

							{turno.pacienteEmail?.length && turno.pacienteEmail.length > 0 && <>{turno.pacienteEmail} <br /></>}
							{turno.prepaga && <>{turno.prepaga} {turno.afiliadoNro && ` (${turno.afiliadoNro})`}</>}
						</p>
						<h3>Motivo de la Consulta</h3>
						{turno.statusTurno !== TurnoStatus.CANCELADO && <p>{turno.motivoConsulta || '(No se ha ingresado un motivo de consulta)'}</p>}
						{turno.statusTurno === TurnoStatus.CANCELADO && <p>...</p>}
					</div>
					<div style={{width: '25%'}}>
						<h2>Medico</h2>
						<p>
							{turno.medicoNombre} <br />
							{`(${turno.especialidadNombre})`}
						</p>
						<p>
							{role === Role.ADMIN && <>Consulta: {formatCurrency(turno.tarifa)} <br /></>}
							{role === Role.ADMIN && <>Comisión: {formatCurrency(turno.comision)} <br /></>}
							{role === Role.ADMIN && <>Honorario: {formatCurrency(turno.tarifa - turno.comision)}</>}
						</p>
					</div>
				</div>
				{turno.statusTurno !== TurnoStatus.CANCELADO && 
				<div style={{...RowsContainer}}>
					<div style={{...Row1Box, alignItems: 'start' }}>
						<div style={{width: showJitsiModal ? '25%' : '46%'}}>
							<h2>Historia Clínica</h2>
							<div style={{
								width: '100%',
								overflowY: 'auto',
								maxHeight: '400px',
							}}>
								{turno.historia?.map((historia) => (
									<HistoriaCard key={historia.id} historia={historia} />
								))}
							</div>
						</div>
						<div style={{width: showJitsiModal ? '25%' : '46%', display: 'flex', flexDirection: 'column', alignItems: 'end'}}>
							<SmartButton 
								onClick={handleSaveHistoria}
							>
								Agregar Historia Clínica
							</SmartButton>
							<br />
							<textarea 
								style={{
									width: '100%',
									borderRadius: '8px',
									padding: '16px',
									fontSize: '16px',
									fontFamily: 'Raleway',
									resize: 'none',
									overflow: 'auto',
								}}
								value={historiaClinica}
								rows={10}
								onChange={(e) => setHistoriaClinica(e.target.value)}
							>

							</textarea>
						</div>
						{/* {showJitsiModal && 
							<div style={{width: '40%'}}>
								<JitsiMeeting 
									roomName={getRoomName(turno.id, turno.medicoNombre, turno.pacienteNombre)} displayName={turno.medicoNombre} 
								/>
							</div>
						} */}
					</div>
				</div>}
			</div>
		</SmartForm>
		<CustomModal
			showModal={showConfirmModal}
			setShowModal={setShowConfirmModal}
			titulo='Confirmar Finalización de Turno'
			width='50%'
			height='auto'
		>
			<h3>Estas Seguro/a?</h3>
			<p>No podras revertir la acción, y tampoco podrás agregar historia clínica posteriormente, ni acceder a la videollamada con el paciente!</p>
			<p>Solo Confirma si realmente haz terminado la Videollamada y completado la Historia Clínica!</p>
			<div style={{display: 'flex', justifyContent: 'flex-end'}}>
				<SmartButton
					variant={ButtonVariants.CANCEL}
					onClick={() => setShowConfirmModal(false)}
				>
					Cancelar
				</SmartButton>
				<SmartButton
					variant={ButtonVariants.CONFIRM}
					onClick={handleFinalizarTurno}
				>
					Confirmar
				</SmartButton>
			</div>
		</CustomModal>
		<CustomModal
			showModal={showCancelModal}
			setShowModal={setShowCancelModal}
			titulo='Confirmar Cancelación de Turno'
			width='50%'
			height='auto'
		>
			<h3>Estas Seguro/a?</h3>
			<p>No podras revertir la acción. Por favor, confirma si realmente deseas cancelar el turno.</p>
			<div style={{display: 'flex', justifyContent: 'flex-end'}}>
				<SmartButton
					variant={ButtonVariants.CANCEL}
					onClick={() => setShowCancelModal(false)}
				>
					No Estoy Seguro
				</SmartButton>
				<SmartButton
					variant={ButtonVariants.CONFIRM}
					onClick={handleCancelarTurnoConfirmed}
				>
					Si, Cancelar Turno
				</SmartButton>
			</div>
		</CustomModal>
	</>
	)
}